import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/api/jsonapi'
import { isAbort, toApiError, type ApiError } from '@/api/errors'
import { useKeycloak } from '@/plugins/keycloak'
import { useAuth } from '@/stores/auth'
import { usePrefs } from '@/stores/prefs'
import { useSemesters } from '@/stores/semesters'
import type { CourseResource } from '@/api/contract'
import type { CourseRow, PageSize, SortItem, SortKey } from '@/types/courseList'
import type { LocationQueryRaw } from 'vue-router'

const SEARCH_DELAY_MS = 300

export const PAGE_SIZES: readonly PageSize[] = [25, 50, 100]
const DEFAULT_PAGE_SIZE: PageSize = 25
export const DEFAULT_SORT: SortItem = { key: 'code', order: 'asc' }

/** What the course list shows, and what its URL holds (spec §4.5: a shared link opens the same view). */
export interface ListState {
    semester: string | null
    /** `null` when the URL says nothing, so the stored preference applies. */
    mine: boolean | null
    q: string
    page: number
    size: PageSize
    sort: SortItem
}

const one = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined)

function readSort(value: string | undefined): SortItem {
    const match = /^(-?)(code|name)$/.exec(value ?? '')
    return match ? { key: match[2] as SortKey, order: match[1] === '-' ? 'desc' : 'asc' } : DEFAULT_SORT
}

/** Reads a route's query; a repeated or malformed parameter falls back to its default. */
export function readListState(query: Readonly<Record<string, unknown>>): ListState {
    const mine = one(query.mine)
    const page = Number(one(query.page))
    const size = Number(one(query.size))
    return {
        semester: one(query.semester) ?? null,
        mine: mine === undefined ? null : mine === '1',
        q: one(query.q) ?? '',
        page: Number.isInteger(page) && page > 0 ? page : 1,
        size: (PAGE_SIZES as readonly number[]).includes(size) ? (size as PageSize) : DEFAULT_PAGE_SIZE,
        sort: readSort(one(query.sort)),
    }
}

/** Defaults stay out of the URL, so the plain list has a plain address. */
export function listQuery(state: ListState): LocationQueryRaw {
    const sort = `${state.sort.order === 'desc' ? '-' : ''}${state.sort.key}`
    return {
        semester: state.semester ?? undefined,
        mine: state.mine ? '1' : undefined,
        q: state.q === '' ? undefined : state.q,
        page: state.page === 1 ? undefined : String(state.page),
        size: state.size === DEFAULT_PAGE_SIZE ? undefined : String(state.size),
        sort: sort === 'code' ? undefined : sort,
    }
}

/**
 * The exact filter grammar the API documents for `GET /courses`:
 * `semester eq 'X' [and taughtByMe eq true] [and contains(name,'term')]`.
 */
export function courseFilter(semester: string, onlyMine: boolean, text: string): string {
    const term = text.trim()
    return (
        `semester eq '${semester}'` +
        (onlyMine ? ' and taughtByMe eq true' : '') +
        (term === '' ? '' : ` and contains(name,'${term.replaceAll("'", "''")}')`)
    )
}

/** The API sorts names per language; the list's "name" column is the one in the UI language. */
export function apiSort(sort: SortItem, locale: string): { field: 'code' | 'nameCs' | 'nameEn'; direction: 'asc' | 'desc' } {
    const field = sort.key === 'code' ? 'code' : locale === 'en' ? 'nameEn' : 'nameCs'
    return { field, direction: sort.order }
}

export function toCourseRow(resource: Pick<CourseResource, 'attributes'>): CourseRow {
    const { code, nameCs, nameEn, range } = resource.attributes
    return { code, nameCs, nameEn, range }
}

/** The course list's data: what the field and the table ask for, the page that answers, and the URL that mirrors both. */
export function useCourseList() {
    const { locale } = useI18n()
    const route = useRoute()
    const router = useRouter()
    const auth = useAuth()
    const prefs = usePrefs()
    const semesters = useSemesters()

    // An external's own courses are the ones assigned to them (D173).
    const canFilterMine = computed(() => auth.hasRole('teacher') || auth.hasRole('admin') || auth.hasRole('external'))
    // A student's ?mine=1 is ignored: the API answers 400 to taughtByMe for anyone but teachers and externals.
    const onlyMine = computed(() => canFilterMine.value && prefs.onlyMine)

    // The URL wins on load, so a shared link opens the same view (spec §4.5).
    const initial = readListState(route.query)
    if (initial.semester !== null) {
        semesters.select(initial.semester)
    }
    if (initial.mine !== null) {
        prefs.setOnlyMine(initial.mine)
    }

    /** What the search field shows; a cleared v-text-field sets it to null. */
    const search = ref<string | null>(initial.q)
    /** The search the list asks for: the field's text after a pause in typing. */
    const query = ref(initial.q.trim())
    const page = ref(initial.page)
    const itemsPerPage = ref<number>(initial.size)
    const sortBy = ref<SortItem[]>([initial.sort])
    const sort = computed(() => sortBy.value[0] ?? DEFAULT_SORT)

    let typing: ReturnType<typeof setTimeout> | undefined
    watch(search, (value) => {
        clearTimeout(typing)
        const text = (value ?? '').trim()
        // Clearing the field answers at once; typing waits for a pause.
        typing = setTimeout(
            () => {
                query.value = text
            },
            text === '' ? 0 : SEARCH_DELAY_MS,
        )
    })
    function clearSearch(): void {
        clearTimeout(typing)
        search.value = ''
        query.value = ''
    }

    // Declared before the loader's watcher, so a new semester, scope or search asks for page 1 in one request.
    watch([() => semesters.selected, onlyMine, query], () => {
        page.value = 1
    })

    const rows = ref<CourseRow[]>([])
    const total = ref(0)
    const loading = ref(false)
    const error = ref<ApiError | null>(null)
    let controller: AbortController | null = null

    const request = computed(() => {
        const semester = semesters.selected
        if (semester === null) {
            return null
        }
        const { field, direction } = apiSort(sort.value, locale.value)
        return {
            filter: courseFilter(semester, onlyMine.value, query.value),
            field,
            direction,
            limit: itemsPerPage.value,
            offset: (page.value - 1) * itemsPerPage.value,
        }
    })

    /** Loads one page; a newer request aborts the older one, so a stale page never wins. */
    async function reload(): Promise<void> {
        controller?.abort()
        const current = new AbortController()
        controller = current
        error.value = null
        const wanted = request.value
        if (wanted === null) {
            rows.value = []
            total.value = 0
            loading.value = false
            return
        }
        loading.value = true
        try {
            const document = await useApi().courses.self(
                (q) => q.filter(wanted.filter).sort(wanted.field, wanted.direction).limitOffset(wanted.limit, wanted.offset),
                current.signal,
            )
            if (controller !== current) {
                return
            }
            rows.value = document.data.map(toCourseRow)
            total.value = document.total ?? document.data.length
        } catch (caught) {
            if (controller !== current || isAbort(caught)) {
                return
            }
            const failure = toApiError(caught)
            if (failure.status === 401) {
                await useKeycloak().login()
            }
            rows.value = []
            total.value = 0
            error.value = failure
        } finally {
            if (controller === current) {
                loading.value = false
            }
        }
    }

    watch(() => JSON.stringify(request.value), reload, { immediate: true })
    watch(
        () =>
            JSON.stringify(
                listQuery({
                    semester: semesters.selected,
                    mine: onlyMine.value,
                    q: query.value,
                    page: page.value,
                    size: itemsPerPage.value as PageSize,
                    sort: sort.value,
                }),
            ),
        (value) => {
            void router.replace({ query: JSON.parse(value) })
        },
        { immediate: true },
    )
    onBeforeUnmount(() => {
        clearTimeout(typing)
        controller?.abort()
    })

    return { search, query, page, itemsPerPage, sortBy, rows, total, loading, error, canFilterMine, onlyMine, reload, clearSearch }
}
